/**
 * BulkFolderUploader — carga masiva por carpeta.
 *
 * The operator drops (or picks) ONE folder whose subfolders are expedientes
 * named by CURP with canonical file names inside (ine / certificado / foto,
 * ine_reverso optional). Flow: scan+validate client-side (folderScan) →
 * summary with per-folder problems → upload through the frozen contract
 * (bulkUpload: JIT sign + direct PUT, retries, cancel) → report + retry of
 * failures. Staging the bytes never starts processing — "Procesar ronda"
 * remains the explicit, separate step.
 */

import { useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlineOutlined";

import { api, isApiError } from "../../api/useApi";
import { IS_DEMO } from "../../api/client";
import { demoSign } from "./demo";
import { putBytes } from "./putBytes";
import {
  analyzeScannedFiles,
  entriesFromDataTransfer,
  filesFromEntries,
  filesFromInput,
  ignoredFilesList,
  ignoredFilesMessage,
  n,
  readyExpedientesWithIgnored,
  type DroppedTree,
  type ScannedExpediente,
  type ScanResult,
} from "./folderScan";
import {
  CANCELLED_MESSAGE,
  runBulkUpload,
  type BulkProgress,
  type ItemOutcome,
  type SignFn,
} from "./bulkUpload";

type Phase = "idle" | "scanning" | "scanned" | "uploading" | "finished";

type Props = {
  /** Fixed-round mode (Operations screen): stage into this round. */
  roundId?: string;
  /** Create-round mode (Home): called ONCE when the operator confirms the
   * upload; returns the new round id. Retries reuse the same round — a retry
   * must never mint a second round. Exactly one of roundId/createRound. */
  createRound?: (suggestedName: string) => Promise<string>;
  /** Bubbles the round + successfully staged expediente ids. */
  onBulkUploaded: (roundId: string, ids: string[]) => void;
  /** Opens the "Procesar ronda" confirmation for the staged round — the
   * operator's next step, offered right in the success alert. */
  onRequestProcess?: (roundId: string) => void;
};

function formatMB(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1024) return `${(mb / 1024).toLocaleString("es-MX", { maximumFractionDigits: 1 })} GB`;
  return `${mb.toLocaleString("es-MX", { maximumFractionDigits: 1 })} MB`;
}

const FAILED_LIST_CAP = 50;

export function BulkFolderUploader({
  roundId,
  createRound,
  onBulkUploaded,
  onRequestProcess,
}: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [dragActive, setDragActive] = useState(false);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [scanProgress, setScanProgress] = useState<{ checked: number; total: number } | null>(null);
  const [progress, setProgress] = useState<BulkProgress | null>(null);
  const [outcomes, setOutcomes] = useState<ItemOutcome[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  /** The round being staged into: the prop, or the one createRound minted. */
  const [activeRoundId, setActiveRoundId] = useState<string | null>(roundId ?? null);
  /** Dropped root folder name — the suggested display name for a new round. */
  const [rootName, setRootName] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  function reset() {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase("idle");
    setScan(null);
    setScanProgress(null);
    setProgress(null);
    setOutcomes(null);
    setError(null);
    setActiveRoundId(roundId ?? null);
    setRootName("");
  }

  async function analyze(getTree: () => Promise<DroppedTree>) {
    setPhase("scanning");
    setError(null);
    setScan(null);
    setOutcomes(null);
    try {
      const tree = await getTree();
      const result = await analyzeScannedFiles(
        tree.files,
        (checked, total) => setScanProgress({ checked, total }),
        tree.emptyDirPaths,
      );
      setScan(result);
      setPhase("scanned");
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setPhase("idle");
    } finally {
      setScanProgress(null);
    }
  }

  function handleDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragActive(false);
    if (phase === "scanning" || phase === "uploading") return;
    // Snapshot entries synchronously — the DataTransferItemList dies with the event.
    const entries = entriesFromDataTransfer(event.dataTransfer.items);
    if (entries.length === 0) return;
    setRootName(entries.length === 1 && entries[0].isDirectory ? entries[0].name : "");
    void analyze(() => filesFromEntries(entries));
  }

  function handlePick(list: FileList | null) {
    if (!list || list.length === 0) return;
    // A FileList cannot carry empty directories (browser limitation) — only
    // the drag-and-drop path reports them.
    const files = filesFromInput(list);
    setRootName(files[0]?.relPath.split("/")[0] ?? "");
    void analyze(() => Promise.resolve({ files, emptyDirPaths: [] }));
    // Allow re-picking the same folder later.
    if (inputRef.current) inputRef.current.value = "";
  }

  function makeSignFn(rid: string): SignFn {
    return async (expedienteId, files) => {
      if (IS_DEMO) return demoSign(rid, expedienteId, files);
      const res = await api.POST("/uploads/sign", {
        body: { round_id: rid, expediente_id: expedienteId, files },
      });
      return res.data!;
    };
  }

  async function startUpload(expedientes: ScannedExpediente[]) {
    if (expedientes.length === 0) return;
    setError(null);

    // Resolve the target round ONCE; retries stage into the same round.
    let rid = activeRoundId;
    if (!rid) {
      try {
        rid = roundId ?? (await createRound!(rootName || "Carga masiva"));
        setActiveRoundId(rid);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return; // stay on the scan summary so the operator can retry
      }
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setPhase("uploading");
    setProgress(null);
    const items = expedientes.map((e) => ({ expedienteId: e.expedienteId, files: e.files }));
    const results = await runBulkUpload(items, {
      sign: makeSignFn(rid),
      put: putBytes,
      signal: controller.signal,
      isFatal: (err) => isApiError(err) && (err.status === 401 || err.status === 403),
      onProgress: setProgress,
    });
    abortRef.current = null;
    setOutcomes((prev) => {
      // Merge over previous outcomes so "reintentar" keeps earlier successes.
      if (!prev) return results;
      const byId = new Map(prev.map((o) => [o.expedienteId, o]));
      for (const r of results) byId.set(r.expedienteId, r);
      return [...byId.values()];
    });
    setPhase("finished");
    const okIds = results.filter((r) => r.ok).map((r) => r.expedienteId);
    if (okIds.length > 0) onBulkUploaded(rid, okIds);
  }

  function retryFailed() {
    if (!scan || !outcomes) return;
    const failedIds = new Set(outcomes.filter((o) => !o.ok).map((o) => o.expedienteId));
    const failedItems = scan.ready.filter((e) => failedIds.has(e.expedienteId));
    void startUpload(failedItems);
  }

  const readyWithIgnored = scan ? readyExpedientesWithIgnored(scan.ready) : [];
  const okCount = outcomes?.filter((o) => o.ok).length ?? 0;
  const failedOutcomes = outcomes?.filter((o) => !o.ok) ?? [];
  const cancelled = failedOutcomes.some((o) => o.error === CANCELLED_MESSAGE);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 0.5 }}>
          <FolderZipOutlinedIcon color="primary" />
          <Typography variant="h6" component="h2">
            Carga masiva por carpeta
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Arrastra la carpeta del proceso: cada subcarpeta es un expediente nombrado por su
          CURP. Los documentos se reconocen por nombre — la convención{" "}
          <code>{"{CURP}_{TIPO}"}</code> (<code>ANVERIDEN</code> / <code>IDEN</code> para la
          identificación, <code>CERT</code>, <code>FOTO</code>) o los nombres clásicos{" "}
          (<code>ine</code>, <code>certificado</code>, <code>foto</code>).
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Si la identificación llega como un solo archivo, se asume que trae ambos lados y se
          sube como frente <em>y</em> reverso; el revisor verá la misma imagen en las dos
          posiciones.
        </Typography>

        {(phase === "idle" || phase === "scanning") && (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            sx={{
              border: "2px dashed",
              borderColor: dragActive ? "primary.main" : "divider",
              borderRadius: 2,
              px: 3,
              py: 5,
              textAlign: "center",
              bgcolor: dragActive ? "action.hover" : "transparent",
              transition: "border-color 120ms, background-color 120ms",
            }}
          >
            {phase === "scanning" ? (
              <Stack spacing={1.5} sx={{ alignItems: "center" }}>
                <Typography variant="body2">Analizando la carpeta…</Typography>
                <Box sx={{ width: "100%", maxWidth: 420 }}>
                  <LinearProgress
                    variant={scanProgress ? "determinate" : "indeterminate"}
                    value={
                      scanProgress && scanProgress.total > 0
                        ? (scanProgress.checked / scanProgress.total) * 100
                        : undefined
                    }
                  />
                </Box>
                {scanProgress && (
                  <Typography variant="caption" color="text.secondary">
                    {n(scanProgress.checked)} de {n(scanProgress.total)} documentos verificados
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={1.5} sx={{ alignItems: "center" }}>
                <DriveFolderUploadOutlinedIcon sx={{ fontSize: 44 }} color="action" />
                <Typography variant="body1">
                  Arrastra aquí la carpeta con los expedientes
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  o
                </Typography>
                <Button variant="outlined" onClick={() => inputRef.current?.click()}>
                  Seleccionar carpeta
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => handlePick(e.target.files)}
                  {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                />
              </Stack>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            <AlertTitle>No se pudo leer la carpeta</AlertTitle>
            {error.message}
          </Alert>
        )}

        {phase === "scanned" && scan && (
          <Stack spacing={2}>
            <Alert severity={scan.ready.length > 0 ? "success" : "warning"}>
              <AlertTitle>
                {n(scan.ready.length)} expediente(s) listo(s) para cargar
              </AlertTitle>
              {n(scan.totalReadyFiles)} archivos · {formatMB(scan.totalReadyBytes)}
              {scan.problematic.length > 0 &&
                ` · ${n(scan.problematic.length)} carpeta(s) con problemas (no se cargarán)`}
              {scan.junkSkipped > 0 && ` · ${n(scan.junkSkipped)} archivo(s) de sistema ignorados`}
            </Alert>

            {scan.looseRootFiles.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {n(scan.looseRootFiles.length)} archivo(s) sueltos fuera de las carpetas de
                expediente se ignoraron.
              </Typography>
            )}

            {readyWithIgnored.length > 0 && (
              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Chip
                      size="small"
                      color="warning"
                      label={n(readyWithIgnored.length)}
                      variant="outlined"
                    />
                    <Typography variant="body2">
                      Expedientes listos con archivos no reconocidos — esos archivos no se
                      cargarán
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ maxHeight: 320, overflow: "auto", p: 0 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Carpeta</TableCell>
                        <TableCell>Archivos no reconocidos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {readyWithIgnored.map((e) => (
                        <TableRow key={e.parentPath}>
                          <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                            {e.folderName}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {ignoredFilesList(e.ignored)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            )}

            {scan.problematic.length > 0 && (
              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Chip
                      size="small"
                      color="warning"
                      label={n(scan.problematic.length)}
                      variant="outlined"
                    />
                    <Typography variant="body2">
                      Carpetas con problemas — corrígelas y vuelve a arrastrar la carpeta
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ maxHeight: 320, overflow: "auto", p: 0 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Carpeta</TableCell>
                        <TableCell>Problema(s)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scan.problematic.map((e) => (
                        <TableRow key={e.parentPath}>
                          <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                            {e.folderName}
                          </TableCell>
                          <TableCell>
                            {[...e.problems.map((p) => p.message), ignoredFilesMessage(e.ignored)]
                              .filter(Boolean)
                              .join(" ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            )}

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<DriveFolderUploadOutlinedIcon />}
                disabled={scan.ready.length === 0}
                onClick={() => void startUpload(scan.ready)}
              >
                Cargar {n(scan.ready.length)} expediente(s)
              </Button>
              <Button variant="text" onClick={reset}>
                Descartar
              </Button>
            </Stack>
          </Stack>
        )}

        {phase === "uploading" && (
          <Stack spacing={1.5}>
            <Typography variant="body2">Cargando expedientes…</Typography>
            <LinearProgress
              variant="determinate"
              value={
                progress && progress.bytesTotal > 0
                  ? (progress.bytesDone / progress.bytesTotal) * 100
                  : 0
              }
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <Typography variant="caption" color="text.secondary">
                {progress
                  ? `${n(progress.done)} de ${n(progress.total)} expedientes` +
                    (progress.failed > 0 ? ` · ${n(progress.failed)} con error` : "")
                  : "Preparando…"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progress ? `${formatMB(progress.bytesDone)} / ${formatMB(progress.bytesTotal)}` : ""}
              </Typography>
            </Stack>
            <Box>
              <Button color="inherit" onClick={() => abortRef.current?.abort()}>
                Cancelar carga
              </Button>
            </Box>
          </Stack>
        )}

        {phase === "finished" && outcomes && (
          <Stack spacing={2}>
            <Alert
              severity={
                failedOutcomes.length === 0 ? "success" : okCount > 0 ? "warning" : "error"
              }
            >
              <AlertTitle>
                {failedOutcomes.length === 0
                  ? `${n(okCount)} expediente(s) cargado(s) correctamente`
                  : cancelled
                    ? `Carga ${okCount > 0 ? "parcial" : "cancelada"}: ${n(okCount)} cargado(s), ${n(failedOutcomes.length)} pendiente(s)`
                    : `${n(okCount)} cargado(s) · ${n(failedOutcomes.length)} con error`}
              </AlertTitle>
              {failedOutcomes.length === 0
                ? `Los expedientes quedaron en la ronda ${activeRoundId ?? ""}. Usa «Procesar ronda» cuando estés listo.`
                : "Puedes reintentar los expedientes faltantes sin volver a cargar los que ya quedaron."}
            </Alert>

            {failedOutcomes.length > 0 && (
              <Box sx={{ maxHeight: 240, overflow: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Expediente</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedOutcomes.slice(0, FAILED_LIST_CAP).map((o) => (
                      <TableRow key={o.expedienteId}>
                        <TableCell sx={{ fontFamily: "monospace" }}>{o.expedienteId}</TableCell>
                        <TableCell>{o.error}</TableCell>
                      </TableRow>
                    ))}
                    {failedOutcomes.length > FAILED_LIST_CAP && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          … y {n(failedOutcomes.length - FAILED_LIST_CAP)} más
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
              {failedOutcomes.length > 0 && (
                <Button variant="contained" onClick={retryFailed}>
                  Reintentar {n(failedOutcomes.length)} faltante(s)
                </Button>
              )}
              {okCount > 0 && onRequestProcess && activeRoundId && (
                <Button
                  variant={failedOutcomes.length > 0 ? "outlined" : "contained"}
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => onRequestProcess(activeRoundId)}
                >
                  Procesar ronda
                </Button>
              )}
              <Button variant="text" onClick={reset}>
                Cargar otra carpeta
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
